import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  AvatarGroup
} from '@mui/material';
import {
  Person,
  School,
  AdminPanelSettings,
  DeveloperMode,
  Group,
  Email,
  CheckCircle,
  Info,
  AutoAwesome,
  Settings,
  ExpandMore
} from '@mui/icons-material';
import getAppConfig from '../config/app-config';

const Tutorial = () => {
  const theme = useTheme();
  const config = getAppConfig();

  // Avatar images array
  const avatars = [
    '/avatars/default1.jpg',
    '/avatars/default2.jpg',
    '/avatars/default3.jpg',
    '/avatars/default4.jpg',
    '/avatars/default5.jpg',
    '/avatars/default6.jpg',
    '/avatars/default7.jpg',
    '/avatars/default8.jpg',
    '/avatars/default9.jpg'
  ];

  const platformExploration = {
    title: "Esplorazione della Piattaforma",
    icon: <Info />,
    description: `Benvenuto su ${config.BRAND_NAME}`,
    details: [
      "Questa è una versione di staging con dati di esempio",
      "Perfetta per testare tutte le funzionalità in sicurezza",
    //   "Utilizza il Pannello dei Comandi Rapidi per accessi rapidi"
    ]
  };

  const sections = [
    {
      title: "Registrazione e Gestione Utenti",
      icon: <Person />,
      description: "Sistema completo di registrazione con verifica email e recupero password",
      details: [
        "Registrazione utenti con verifica email automatica",
        "Recupero password tramite link sicuro",
        "Gestione profili personalizzabili",
        "Possibilità di aggiungere campi personalizzati (codice fiscale, indirizzo, ecc.)",
        "Associazione documenti agli utenti (certificati, PDF, immagini)"
      ]
    },
    {
      title: "Sistema di Ruoli e Permessi",
      icon: <Group />,
      description: "Tre livelli di accesso con funzionalità specifiche per ogni ruolo",
      details: [
        {
          role: "Membro",
          icon: <Person />,
          features: [
            "Tesseramento e gestione abbonamenti",
            "Esplorazione e iscrizione ai corsi",
            "Visualizzazione del proprio profilo"
          ]
        },
        {
          role: "Docente",
          icon: <School />,
          features: [
            "Gestione corsi e classi",
            "Registrazione presenze",
            "Calendario corsi personalizzato",
            "Gestione pagamenti allievi"
          ]
        },
        {
          role: "Amministratore",
          icon: <AdminPanelSettings />,
          features: [
            "Visione complessiva dell'associazione",
            "Creazione campagne di tesseramento",
            "Gestione corsi e programmi",
            "Monitoraggio pagamenti",
            "Comunicazioni di gruppo"
          ]
        }
      ]
    },
    {
      title: "Flussi Automatici",
      icon: <AutoAwesome />,
      description: "Sistema intelligente di notifiche automatiche per eventi importanti",
      details: [
        "Notifiche automatiche per tesseramento",
        "Conferme di iscrizione ai corsi",
        "Promemoria per rinnovi abbonamenti",
        "Invio automatico di documenti (PDF, QR Code, tessere)",
        "Personalizzazione completa dei contenuti email"
      ]
    },
    {
      title: "Architettura Moderna e Scalabile",
      icon: <Settings />,
      description: "Progettato seguendo le best practices per gestionali efficienti",
      details: [
        "Architettura modulare e espandibile",
        "Alta configurabilità per esigenze specifiche",
        "Sviluppo continuo e aggiornamenti regolari",
        "Supporto tecnico dedicato",
        "Contattaci per personalizzazioni: giggioz@gmail.com"
      ]
    }
  ];

  const userGuides = [
    {
      role: "Membro",
      steps: [
        {
          title: "Registrazione",
          description: "Registrati con la tua email o usa il Pannello dei Comandi Rapidi (PCR) per accedere come utente predefinito"
        },
        {
          title: "Tesseramento",
          description: "Nel profilo, attiva il tesseramento e segui il flusso di pagamento (online o contanti). Se il tesseramento è già attivo lo puoi rimuovere con il PCR."
        },
        {
          title: "Iscrizione Corsi",
          description: "Una volta tesserato, esplora i corsi disponibili e iscriviti. Usa il PCR per rimuovere iscrizioni esistenti se necessario"
        }
      ]
    },
    {
      role: "Docente",
      steps: [
        {
          title: "Accesso",
          description: "Usa il Pannello dei Comandi Rapidi per accedere come docente (Lucy)"
        },
        {
          title: "Area Docente",
          description: "Accedi alla sezione Area Docente per visualizzare il calendario e i corsi gestiti"
        },
        {
          title: "Gestione Corsi",
          description: "Apri il dettaglio di un corso per visualizzare gli allievi, gestire presenze e pagamenti"
        }
      ]
    },
    {
      role: "Amministratore",
      steps: [
        {
          title: "Accesso Admin",
          description: "Usa il Pannello dei Comandi Rapidi per accedere come amministratore"
        },
        {
          title: "Pannelli di Gestione",
          description: "Esplora i pannelli: Utenti, Tesserati, Campagne Tesseramenti, Gestione Corsi, Pagamenti"
        },
        {
          title: "Comunicazioni",
          description: "Invia comunicazioni di gruppo (per corso, per tutti i membri, ecc.)"
        }
      ]
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header with Avatar Group */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <AvatarGroup max={6} sx={{ '& .MuiAvatar-root': { width: 60, height: 60 } }}>
            {avatars.slice(0, 6).map((avatar, index) => (
              <Avatar 
                key={index} 
                src={avatar} 
                alt={`User ${index + 1}`}
                sx={{ 
                  border: 3, 
                  borderColor: 'white',
                  boxShadow: theme.shadows[4]
                }}
              />
            ))}
          </AvatarGroup>
        </Box>
        <Typography variant="h3" component="h1" gutterBottom color="primary">
          Guida alla Piattaforma
        </Typography>
        
      </Box>

      {/* Platform Exploration Card */}
      <Box sx={{ mb: 6 }}>
        <Card 
          elevation={3}
          sx={{ 
            transition: 'transform 0.2s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: theme.shadows[12]
            }
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box sx={{ 
                borderRadius: 2, 
                bgcolor: 'primary.main', 
                color: 'white',
                mr: 3,
                width: 60,
                height: 60,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {platformExploration.icon}
              </Box>
              <Typography variant="h4" component="h2" color="primary">
                {platformExploration.title}
              </Typography>
            </Box>
            
            <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
              {platformExploration.description}
            </Typography>

            <List>
              {platformExploration.details.map((detail, idx) => (
                <ListItem key={idx} sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CheckCircle color="primary" fontSize="medium" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={detail} 
                    primaryTypographyProps={{ variant: 'h6' }}
                  />
                </ListItem>
              ))}
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <CheckCircle color="primary" fontSize="medium" />
                </ListItemIcon>
                <ListItemText 
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>Utilizza il Pannello dei Comandi Rapidi in alto a destra</span>
                      <DeveloperMode sx={{ fontSize: 20, color: 'primary.main' }} />
                    </Box>
                  }
                  primaryTypographyProps={{ variant: 'h6' }}
                />
              </ListItem>
            </List>
          </CardContent>
        </Card>
      </Box>

      {/* Accordion Sections */}
      <Box sx={{ mb: 6 }}>
        {/* Funzionalità Accordion */}
        <Accordion sx={{ mb: 2 }}>
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{
              '& .MuiAccordionSummary-content': {
                alignItems: 'center'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                borderRadius: 2, 
                bgcolor: 'primary.main', 
                color: 'white',
                mr: 3,
                width: 60,
                height: 60,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Settings />
              </Box>
              <Typography variant="h5" component="h2">
                Funzionalità
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={4}>
              {sections.map((section, index) => (
                <Grid size={{ xs: 12, md: 6, lg: 4 }} key={index}>
                  <Card 
                    elevation={2}
                    sx={{ 
                      height: '100%',
                      transition: 'transform 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: theme.shadows[8]
                      }
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ 
                borderRadius: 2, 
                bgcolor: 'primary.main', 
                color: 'white',
                mr: 3,
                width: 60,
                height: 60,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
                        }}>
                          {section.icon}
                        </Box>
                        <Typography variant="h6" component="h3">
                          {section.title}
                        </Typography>
                      </Box>
                      
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                        {section.description}
                      </Typography>

                      {section.details && Array.isArray(section.details) && section.details[0] && typeof section.details[0] === 'string' ? (
                        <List dense>
                          {section.details.map((detail, idx) => (
                            <ListItem key={idx} sx={{ px: 0 }}>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <CheckCircle color="primary" fontSize="small" />
                              </ListItemIcon>
                              <ListItemText primary={detail} />
                            </ListItem>
                          ))}
                        </List>
                      ) : (
                        <Box>
                          {section.details.map((role, idx) => (
                            <Box key={idx} sx={{ mb: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Box sx={{ 
          
                                  borderRadius: 0.5, 
                                  bgcolor: 'secondary.main', 
                                  color: 'white',
                                  mr: 3,
                                  width: 48,
                                  height: 48,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  {role.icon}
                                </Box>
                                <Typography variant="subtitle1" fontWeight="bold">
                                  {role.role}
                                </Typography>
                              </Box>
                              <List dense>
                                {role.features.map((feature, featureIdx) => (
                                  <ListItem key={featureIdx} sx={{ px: 0 }}>
                                    <ListItemIcon sx={{ minWidth: 24 }}>
                                      <CheckCircle color="secondary" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText primary={feature} />
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Usa la Piattaforma Accordion */}
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{
              '& .MuiAccordionSummary-content': {
                alignItems: 'center'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
         borderRadius: 2, 
                bgcolor: 'primary.main', 
                color: 'white',
                mr: 3,
                width: 60,
                height: 60,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Person />
              </Box>
              <Typography variant="h5" component="h2">
                Usa la Piattaforma
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {/* Test Credit Card Information Box */}
            <Box sx={{ mb: 4 }}>
              <Paper 
                elevation={2}
                sx={{ 
                  p: 3,
                  bgcolor: 'warning.light',
                  border: 1,
                  borderColor: 'warning.main',
                  borderRadius: 2
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Info sx={{ mr: 1, color: 'warning.dark' }} />
                  <Typography variant="h6" component="h3" color="warning.dark" fontWeight="bold">
                    Informazioni per Test di Pagamento
                  </Typography>
                </Box>
                <Typography variant="body1" color="warning.dark">
                  Per effettuare acquisti di test con carta di credito, utilizza i seguenti dati:
                </Typography>
                <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'warning.main' }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                    Numero carta: <span style={{ fontFamily: 'monospace', fontSize: '1.1em' }}>4242 4242 4242 4242</span>
                  </Typography>
                  <Typography variant="body2">
                    Tutti gli altri dati (nome, scadenza, CVV) possono essere qualsiasi valore valido.
                  </Typography>
                </Box>
              </Paper>
            </Box>
            
            <Grid container spacing={4}>
              {userGuides.map((guide, index) => (
                <Grid size={{ xs: 12, md: 4 }} key={index}>
                  <Paper 
                    elevation={1}
                    sx={{ 
                      p: 3,
                      height: '100%',
                      border: 1,
                      borderColor: 'divider',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    {/* Background Avatar */}
                    <Box sx={{ 
                      position: 'absolute', 
                      top: -20, 
                      right: -20, 
                      opacity: 0.1,
                      zIndex: 0
                    }}>
                      <Avatar 
                        src={avatars[index % avatars.length]} 
                        sx={{ width: 120, height: 120 }}
                      />
                    </Box>
                    
                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <Avatar 
                          src={avatars[index % avatars.length]} 
                          sx={{ 
                            width: 56, 
                            height: 56, 
                            mr: 2,
                            border: 2,
                            borderColor: index === 0 ? 'primary.main' : index === 1 ? 'secondary.main' : 'success.main'
                          }}
                        />
                        <Box>
                          <Typography variant="h6" component="h3">
                            {guide.role}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {index === 0 ? 'Gestione profilo e iscrizioni' : 
                             index === 1 ? 'Gestione corsi e allievi' : 
                             'Gestione completa associazione'}
                          </Typography>
                        </Box>
                      </Box>

                      <List>
                        {guide.steps.map((step, stepIndex) => (
                          <ListItem key={stepIndex} sx={{ px: 0, flexDirection: 'column', alignItems: 'flex-start' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <Chip 
                                label={`${stepIndex + 1}`} 
                                size="small" 
                                color="primary" 
                                sx={{ mr: 1 }}
                              />
                              <Typography variant="subtitle2" fontWeight="bold">
                                {step.title}
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                              {step.description}
                            </Typography>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* Contact Section with Avatar */}
      <Box sx={{ textAlign: 'center', mt: 6, p: 4, bgcolor: 'primary.main', color: 'white', borderRadius: 2, position: 'relative', overflow: 'hidden' }}>
        {/* Background Avatars */}
        <Box sx={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          opacity: 0.1,
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center'
        }}>
          {avatars.slice(0, 4).map((avatar, index) => (
            <Avatar 
              key={index} 
              src={avatar} 
              sx={{ width: 80, height: 80 }}
            />
          ))}
        </Box>
        
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h5" gutterBottom>
            Hai esigenze specifiche?
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
            Associate è altamente configurabile e può essere personalizzata per le tue esigenze specifiche.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <Email sx={{ fontSize: 20 }} />
            <Typography variant="h6" component="a" href="mailto:infinitymundi.publishing@gmail.com" sx={{ 
              color: 'white', 
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}>
              infinitymundi.publishing@gmail.com
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Footer with Avatar Group */}
      
    </Container>
  );
};

export default Tutorial; 